/**
 * Upsell Popup JavaScript
 * Handles the multi-layer upsell popup functionality with countdown timers
 */

(function() {
  'use strict';

  class UpsellPopup {
    constructor() {
      this.config = { enabled: false, layers: [] };
      this.overlay = document.getElementById('upsellPopupOverlay');
      this.ready = Boolean(this.overlay);
      if (!this.overlay) return;

      this.popup = this.overlay.querySelector('.upsell-popup');
      this.configEl = document.getElementById('upsell-popup-config');
      if (this.configEl) {
        try {
          this.config = JSON.parse(this.configEl.textContent);
        } catch (e) {
          console.error('[UpsellPopup] Failed to parse config:', e);
        }
      }

      this.currentLayerIndex = 0;
      this.selectedProducts = {};
      this.mainProductFormData = null;
      this.mainProductForm = null;
      this.countdownTimer = null;
      this.countdownValue = 0;
      this.activeDrawer = null;
      this.activePopover = null;
      this.isTransitioning = false;

      this.init();
    }

    init() {
      if (!this.config.enabled || this.config.layers.length === 0) return;

      this.bindEvents();
    }

    bindEvents() {
      // Note: Clicking on overlay does NOT close the popup (intentional)
      // User must use close button, Yes/No buttons, or Escape key

      // Close button - goes back to previous layer, or closes on first layer
      const closeBtn = this.overlay.querySelector('[data-upsell-close]');
      if (closeBtn) {
        closeBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onCloseClick();
        });
      }

      // Escape key - goes back to previous layer, or closes on first layer
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && this.isOpen()) {
          this.onCloseClick();
        }
      });

      // Yes/No buttons for each layer
      this.overlay.querySelectorAll('[data-upsell-yes]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onYesClick();
        });
      });

      this.overlay.querySelectorAll('[data-upsell-no]').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onNoClick();
        });
      });

      // Character counter for input fields
      this.overlay.querySelectorAll('[data-upsell-input]').forEach((input) => {
        const counter = input.closest('.upsell-popup__input-container')?.querySelector('[data-char-count]');
        if (counter) {
          input.addEventListener('input', () => {
            counter.textContent = input.value.length;
          });
        }
      });

      // Completion popup buttons
      const continueBtn = this.overlay.querySelector('[data-upsell-continue]');
      if (continueBtn) {
        continueBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onContinueShopping();
        });
      }

      const checkoutBtn = this.overlay.querySelector('[data-upsell-checkout]');
      if (checkoutBtn) {
        checkoutBtn.addEventListener('click', (e) => {
          e.preventDefault();
          this.onGoToCheckout();
        });
      }
    }

    initFormInterception() {}

    observeDynamicForms() {}

    isEnabled() {
      return this.config.enabled && this.config.layers.length > 0;
    }

    isOpen() {
      return this.overlay && this.overlay.classList.contains('active');
    }

    open(mainFormData, mainForm) {
      this.mainProductFormData = mainFormData;
      this.mainProductForm = mainForm;
      this.currentLayerIndex = 0;
      this.selectedProducts = {};
      this.isTransitioning = false;

      // Store reference to drawer/popover for later
      const drawer = document.querySelector('quick-buy-drawer[open]');
      const popover = document.querySelector('quick-buy-popover[open]');
      if (drawer) {
        this.activeDrawer = drawer;
        // Add upsell popup to focus trap's container elements
        if (drawer._focusTrap && drawer._focusTrap.updateContainerElements) {
          drawer._focusTrap.updateContainerElements([drawer, this.overlay]);
        }
      }
      if (popover) {
        this.activePopover = popover;
        // Add upsell popup to focus trap's container elements
        if (popover._focusTrap && popover._focusTrap.updateContainerElements) {
          popover._focusTrap.updateContainerElements([popover, this.overlay]);
        }
      }

      this.showLayer(0);
      this.overlay.classList.add('active');
      document.body.style.overflow = 'hidden';
    }

    close() {
      this.stopCountdown();
      this.overlay.classList.remove('active');
      document.body.style.overflow = '';
      this.isTransitioning = false;

      // Remove upsell popup from focus trap's container elements
      if (this.activeDrawer && this.activeDrawer._focusTrap && this.activeDrawer._focusTrap.updateContainerElements) {
        this.activeDrawer._focusTrap.updateContainerElements([this.activeDrawer]);
      }
      if (this.activePopover && this.activePopover._focusTrap && this.activePopover._focusTrap.updateContainerElements) {
        this.activePopover._focusTrap.updateContainerElements([this.activePopover]);
      }

      // Clear references
      this.activeDrawer = null;
      this.activePopover = null;

      this.resetLayers();
    }

    onCloseClick() {
      this.stopCountdown();
      this.isTransitioning = false;

      // If on first layer (index 0), close the popup entirely and cancel
      if (this.currentLayerIndex === 0) {
        this.close();
        return;
      }

      // Go back to previous layer and clear that layer's previous selection
      const prevIndex = this.currentLayerIndex - 1;
      delete this.selectedProducts[prevIndex];
      this.showLayer(prevIndex);
    }

    resetLayers() {
      this.stopCountdown();
      this.currentLayerIndex = 0;
      this.selectedProducts = {};
      this.mainProductFormData = null;
      this.mainProductForm = null;

      // Hide loading and completion states
      this.hideLoading();
      this.hideCompletion();

      // Reset all inputs
      this.overlay.querySelectorAll('[data-upsell-input]').forEach((input) => {
        input.value = '';
      });

      // Reset layer visibility - show first layer
      const layers = this.overlay.querySelectorAll('.upsell-popup__layer');
      layers.forEach((layer, index) => {
        layer.classList.toggle('active', index === 0);
      });

      // Reset countdown displays
      this.config.layers.forEach((layer, index) => {
        if (layer.countdown > 0) {
          const layerEl = this.overlay.querySelector(`.upsell-popup__layer[data-layer-index="${index}"]`);
          if (layerEl) {
            const countdownNumber = layerEl.querySelector('[data-countdown-number]');
            if (countdownNumber) {
              // Reset to initial MM:SS format
              countdownNumber.textContent = this.formatTime(layer.countdown * 60);
            }
          }
        }
      });
    }

    showLayer(index) {
      this.stopCountdown();

      const layers = this.overlay.querySelectorAll('.upsell-popup__layer');
      layers.forEach((layer, i) => {
        layer.classList.toggle('active', i === index);
      });
      this.currentLayerIndex = index;

      // Start countdown for this layer if configured
      const layer = this.config.layers[index];
      if (layer && layer.countdown > 0) {
        this.startCountdown(layer.countdown, index);
      }
    }

    startCountdown(minutes, layerIndex) {
      // Convert minutes to seconds
      this.countdownValue = minutes * 60;
      const layerEl = this.overlay.querySelector(`.upsell-popup__layer[data-layer-index="${layerIndex}"]`);
      const countdownNumber = layerEl?.querySelector('[data-countdown-number]');

      // Format and display initial time
      if (countdownNumber) {
        countdownNumber.textContent = this.formatTime(this.countdownValue);
      }

      this.countdownTimer = setInterval(() => {
        this.countdownValue--;

        if (countdownNumber) {
          countdownNumber.textContent = this.formatTime(this.countdownValue);
        }

        if (this.countdownValue <= 0) {
          this.stopCountdown();
          // Auto-skip to next layer (like clicking "No")
          this.onNoClick();
        }
      }, 1000);
    }

    formatTime(totalSeconds) {
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    }

    stopCountdown() {
      if (this.countdownTimer) {
        clearInterval(this.countdownTimer);
        this.countdownTimer = null;
      }
    }

    getCurrentLayer() {
      return this.config.layers[this.currentLayerIndex];
    }

    getCurrentLayerElement() {
      return this.overlay.querySelector(`.upsell-popup__layer[data-layer-index="${this.currentLayerIndex}"]`);
    }

    onYesClick() {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      this.stopCountdown();
      const layer = this.getCurrentLayer();
      const layerEl = this.getCurrentLayerElement();

      if (!layer || !layerEl) {
        this.isTransitioning = false;
        return;
      }

      // Collect product data
      const productData = {
        variantId: layer.variantId,
        quantity: 1,
        properties: {}
      };

      // Check for custom input
      if (layer.showInput) {
        const input = layerEl.querySelector('[data-upsell-input]');
        if (input && input.value.trim()) {
          productData.properties[layer.inputPropertyName] = input.value.trim();
        }
      }

      this.selectedProducts[this.currentLayerIndex] = productData;
      this.nextLayerOrComplete();
    }

    onNoClick() {
      if (this.isTransitioning) return;
      this.isTransitioning = true;
      this.stopCountdown();
      // Clear any previous selection for this layer (user may have clicked Yes then came back)
      delete this.selectedProducts[this.currentLayerIndex];
      this.nextLayerOrComplete();
    }

    nextLayerOrComplete() {
      const nextIndex = this.currentLayerIndex + 1;

      if (nextIndex < this.config.layers.length) {
        this.showLayer(nextIndex);
        // Release lock after transition so next click is accepted
        setTimeout(() => { this.isTransitioning = false; }, 300);
      } else {
        // Keep lock through complete() — release happens in close() / reset()
        this.complete();
      }
    }

    async complete() {
      const cartAddUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

      // Show loading state
      this.showLoading();

      try {
        // Collect all items to add in a single batch request
        const itemsToAdd = [];

        // 1. Add main product
        if (this.mainProductFormData) {
          const mainProductId = this.mainProductFormData.get('id');
          const mainProductQuantity = this.mainProductFormData.get('quantity') || 1;

          if (mainProductId) {
            const mainItem = {
              id: parseInt(mainProductId),
              quantity: parseInt(mainProductQuantity)
            };

            // Collect properties from main form
            const properties = {};
            for (const [key, value] of this.mainProductFormData.entries()) {
              if (key.startsWith('properties[') && value) {
                const propName = key.replace('properties[', '').replace(']', '');
                properties[propName] = value;
              }
            }
            if (Object.keys(properties).length > 0) {
              mainItem.properties = properties;
            }

            const requiresImageUpload =
              this.mainProductForm?.dataset.upsellRequiresImageUpload !== 'false';
            if (requiresImageUpload && !properties['Generated Image']) {
              console.error('[UpsellPopup] Missing Generated Image, aborting add to cart', {
                productId: mainProductId,
                formId: this.mainProductForm?.id,
                capturedKeys: Array.from(this.mainProductFormData.keys()),
              });
              this.hideLoading();
              this.close();
              alert('Please upload your photo before continuing.');

              const uploadContainer =
                document.querySelector(`[data-casecase-ai-upload][data-product-form-id="${this.escapeSelector(this.mainProductForm?.id || '')}"]`) ||
                this.mainProductForm
                  ?.closest('product-form-component, .product-form, .quick-buy-product, quick-buy-drawer, quick-buy-popover, .drawer__content, .popover__content')
                  ?.querySelector('[data-casecase-ai-upload], .ai-upload-container') ||
                document.querySelector('[data-casecase-ai-upload], .ai-upload-container');
              uploadContainer?.scrollIntoView({ behavior: 'smooth', block: 'center' });

              const trigger = uploadContainer?.querySelector('[data-ai-upload-trigger], #ai-upload-trigger');
              if (trigger) trigger.classList.add('validation-error');
              return;
            }

            itemsToAdd.push(mainItem);
          }
        }

        // 2. Collect checked accessories
        itemsToAdd.push(...this.collectAccessoryItems());

        // 3. Collect upsell products
        for (const product of Object.values(this.selectedProducts)) {
          const upsellItem = {
            id: parseInt(product.variantId),
            quantity: product.quantity || 1
          };
          if (Object.keys(product.properties).length > 0) {
            upsellItem.properties = product.properties;
          }
          itemsToAdd.push(upsellItem);
        }

        let addResponseData = {};

        // Add all items in a single batch request
        if (itemsToAdd.length > 0) {
          const sections = this.getCartSectionIds();
          const payload = { items: itemsToAdd };
          if (sections.length > 0) {
            payload.sections = sections.join(',');
            payload.sections_url = window.location.pathname;
          }

          const response = await fetch(cartAddUrl, {
            body: JSON.stringify(payload),
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            credentials: 'same-origin'
          });

          if (!response.ok) {
            throw new Error(`Cart add failed: ${response.status}`);
          }

          addResponseData = await response.json();
        }

        // Refresh the cart drawer once at the end
        await this.refreshCart(addResponseData.sections);

        // Hide loading, show completion popup
        this.hideLoading();
        this.showCompletion();

        // Clear selected images
        this.clearSelectedImages();

      } catch (error) {
        console.error('[UpsellPopup] Error adding products to cart:', error);
        this.hideLoading();
        this.close();
      }
    }

    /**
     * Add checked accessories to cart (kept for backwards compatibility, but not used in batch mode)
     * These are the "Additional Customization Options" checkboxes
     */
    async addAccessoriesToCart(cartAddUrl) {
      const accessoryItems = this.collectAccessoryItems();
      const inputCount = accessoryItems.length;

      if (inputCount === 0) {
        return;
      }

      const accessoriesForm = new FormData();
      accessoryItems.forEach((item, index) => {
        accessoriesForm.set(`items[${index}]id`, item.id);
        accessoriesForm.set(`items[${index}]quantity`, item.quantity);
      });

      await fetch(cartAddUrl, {
        body: accessoriesForm,
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/json'
        },
        credentials: 'same-origin'
      });
    }

    collectAccessoryItems() {
      return Array.from(document.querySelectorAll('.accessories-item input[data-action="bundle-add-to-cart"]:checked'))
        .map((input) => ({
          id: parseInt(input.getAttribute('data-product-variant-id'), 10),
          quantity: parseInt(input.getAttribute('data-product-qty'), 10) || 1
        }))
        .filter((item) => Number.isFinite(item.id));
    }

    escapeSelector(value) {
      if (window.CSS && typeof window.CSS.escape === 'function') {
        return window.CSS.escape(value);
      }
      return String(value).replace(/["\\]/g, '\\$&');
    }

    getCartSectionIds() {
      const sectionIds = [];
      document.querySelectorAll('cart-items-component').forEach((component) => {
        if (component instanceof HTMLElement && component.dataset.sectionId) {
          sectionIds.push(component.dataset.sectionId);
        }
      });
      return [...new Set(sectionIds)];
    }

    async refreshCart(sections = null) {
      try {
        const cartUrl = window.Theme?.routes?.cart_url || '/cart';
        const response = await fetch(`${cartUrl}.js`, {
          headers: {
            'Accept': 'application/json',
            'X-Requested-With': 'XMLHttpRequest'
          },
          credentials: 'same-origin'
        });
        const cartContent = await response.json();

        document.dispatchEvent(new CustomEvent('cart:update', {
          bubbles: true,
          detail: {
            resource: cartContent,
            sourceId: 'upsell-popup',
            data: {
              source: 'upsell-popup',
              itemCount: cartContent.item_count,
              sections: sections || {},
            },
          },
        }));
      } catch (error) {
        console.error('[UpsellPopup] Error refreshing cart:', error);
      }
    }

    showLoading() {
      const loadingEl = this.overlay.querySelector('[data-upsell-loading]');
      if (loadingEl) {
        loadingEl.classList.add('active');
      }
    }

    hideLoading() {
      const loadingEl = this.overlay.querySelector('[data-upsell-loading]');
      if (loadingEl) {
        loadingEl.classList.remove('active');
      }
    }

    showSuccess() {
      const successEl = this.overlay.querySelector('[data-upsell-success]');
      if (successEl) {
        successEl.classList.add('active');
      }
    }

    hideSuccess() {
      const successEl = this.overlay.querySelector('[data-upsell-success]');
      if (successEl) {
        successEl.classList.remove('active');
      }
    }

    showCompletion() {
      const completionEl = this.overlay.querySelector('[data-upsell-completion]');
      if (completionEl) {
        completionEl.classList.add('active');
      }
    }

    hideCompletion() {
      const completionEl = this.overlay.querySelector('[data-upsell-completion]');
      if (completionEl) {
        completionEl.classList.remove('active');
      }
    }

    onContinueShopping() {
      this.hideCompletion();
      this.close();
    }

    onGoToCheckout() {
      this.hideCompletion();
      this.close();
      this.goToCheckout();
    }

    clearSelectedImages() {
      // Helper function to clear upload components in a specific context
      const clearInContext = (context, contextName) => {
        // Clear hidden input fields
        const hiddenInput1 = context.querySelector('[data-ai-generated-image-input], #ai-custom-image-url');
        const hiddenInput2 = context.querySelector('[name="properties[Generated Image 2]"], #ai-custom-image-url-2');
        const hiddenInputJob1 = context.querySelector('[data-ai-generated-job-id-input], #ai-custom-image-url-job-id');
        const hiddenInputJob2 = context.querySelector('[name="properties[_JobID 2]"], #ai-custom-image-url-2-job-id');

        if (hiddenInput1) {
          hiddenInput1.value = '';
        }
        if (hiddenInput2) {
          hiddenInput2.value = '';
        }
        if (hiddenInputJob1) hiddenInputJob1.value = '';
        if (hiddenInputJob2) hiddenInputJob2.value = '';

        // Clear by name patterns
        const imageInputs = context.querySelectorAll(
          'input[name="properties[Generated Image]"], ' +
          'input[name="properties[Generated Image 2]"], ' +
          'input[name="properties[Custom Image]"], ' +
          'input[name="properties[_Custom Image]"], ' +
          'input[name="properties[Custom Image 2]"], ' +
          'input[name="properties[_Custom Image 2]"], ' +
          'input[name="properties[_JobID]"], ' +
          'input[name="properties[_JobID 2]"], ' +
          'input[name="properties[_Display Image]"], ' +
          'input[name="properties[_Display Image 2]"]'
        );
        imageInputs.forEach(input => {
          input.value = '';
        });

        // Clear first upload preview
        const previewImg1 = context.querySelector('.ai-upload-preview, #ai-upload-preview');
        const uploadBtn1 = context.querySelector('[data-ai-upload-trigger], #ai-upload-trigger');
        const statusDiv1 = context.querySelector('[data-ai-upload-status], #ai-upload-status');

        if (previewImg1) {
          previewImg1.src = '';
          previewImg1.style.removeProperty('display');
        }
        if (uploadBtn1) {
          uploadBtn1.classList.remove('has-image', 'uploaded');
          const icon1 = uploadBtn1.querySelector('.ai-upload-icon');
          const text1 = uploadBtn1.querySelector('.ai-upload-text');
          if (icon1) icon1.style.removeProperty('display');
          if (text1) text1.style.removeProperty('display');
        }
        if (statusDiv1) {
          statusDiv1.style.display = 'none';
          statusDiv1.className = 'ai-upload-status';
          statusDiv1.textContent = '';
        }
        uploadBtn1?.closest('[data-casecase-ai-upload]')?.querySelector('[data-ai-upload-remove]')?.click();

        // Clear second upload preview
        const previewImg2 = context.querySelector('#ai-upload-preview-2');
        const uploadBtn2 = context.querySelector('#ai-upload-trigger-2');
        const statusDiv2 = context.querySelector('#ai-upload-status-2');

        if (previewImg2) {
          previewImg2.src = '';
          previewImg2.style.removeProperty('display');
        }
        if (uploadBtn2) {
          uploadBtn2.classList.remove('has-image', 'uploaded');
          const icon2 = uploadBtn2.querySelector('.ai-upload-icon');
          const text2 = uploadBtn2.querySelector('.ai-upload-text');
          if (icon2) icon2.style.removeProperty('display');
          if (text2) text2.style.removeProperty('display');
        }
        if (statusDiv2) {
          statusDiv2.style.display = 'none';
          statusDiv2.className = 'ai-upload-status';
          statusDiv2.textContent = '';
        }

        // Clear generated images grid
        const imagesGrid = context.querySelector('.ai-generated-images-grid');
        if (imagesGrid) {
          imagesGrid.innerHTML = '';
        }

        // Clear file inputs
        const fileInputs = context.querySelectorAll('.ai-upload-input, #ai-file-input, #ai-file-input-2');
        fileInputs.forEach(input => {
          input.value = '';
        });
      };

      // Clear in drawer if exists
      const drawer = this.activeDrawer || document.querySelector('quick-buy-drawer[open]');
      if (drawer) {
        clearInContext(drawer, 'drawer');
      }

      // Clear in popover if exists
      const popover = this.activePopover || document.querySelector('quick-buy-popover[open]');
      if (popover) {
        clearInContext(popover, 'popover');
      }

      // Always clear in main page as well (in case user added from main page)
      clearInContext(document, 'main page');

      // Clear FilePond instances globally
      if (window.FilePond) {
        const ponds = document.querySelectorAll('.filepond');
        ponds.forEach(pond => {
          const instance = FilePond.find(pond);
          if (instance) {
            instance.removeFiles();
          }
        });
      }

      // Dispatch event for any custom handlers
      document.dispatchEvent(new CustomEvent('upsell:images-cleared'));
    }

    goToCheckout() {
      // Create a form to submit to checkout (Shopify's standard method)
      const form = document.createElement('form');
      form.method = 'POST';
      form.action = window.Theme?.routes?.cart_url || '/cart';

      // Add the checkout input
      const checkoutInput = document.createElement('input');
      checkoutInput.type = 'hidden';
      checkoutInput.name = 'checkout';
      checkoutInput.value = '';
      form.appendChild(checkoutInput);

      // Append to body and submit
      document.body.appendChild(form);
      form.submit();
    }
  }

  // Initialize when DOM is ready
  function initUpsellPopup() {
    if (window.upsellPopup?.ready) return;
    window.upsellPopup = new UpsellPopup();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initUpsellPopup);
  } else {
    initUpsellPopup();
  }

  window.addEventListener('load', initUpsellPopup);
})();
